import { Button, Rate, Tag, Tooltip } from "antd";
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { motion, AnimatePresence } from "framer-motion";
import dayjs from "dayjs";
import {
  CopyOutlined,
  EditOutlined,
  FilePdfOutlined,
  FileTextOutlined,
  FileWordOutlined,
  MinusCircleOutlined,
  PlusCircleOutlined,
  StarFilled,
  StopOutlined,
} from "@ant-design/icons";
import isEmpty from "../../validations/is-empty";

const PIXELS_PER_HEIGHT = 50;

const STATUS_LIST = [
  "Recruiter Phone",
  "Recruiter Video",
  "HR Call",
  "Technical",
  "Behavioural",
  "Final",
  "Failed",
];

const CallCalendarView = ({
  searchedData,
  handleClickEdit,
  handleDownloadResume,
  handleDownloadPDF,
  handleDownloadJD,
  handleCopyDetail,
  handleReviewModalOpen,
  allStatus,
  rateValues,
  canUseSpecialActions,
}) => {
  const themeMode = useSelector((state) => state.themeMode);
  const timezoneMode = useSelector((state) => state.timezoneMode);
  const [groupedData, setGroupedData] = useState({ columns: {} });
  const [expandedIds, setExpandedIds] = useState([]);
  const [currentTimeLinePosition, setCurrentTimeLinePosition] = useState(0);
  const [currentTime, setCurrentTime] = useState(
    dayjs(dayjs().tz(timezoneMode.timezoneMode)).tz(
      timezoneMode.timezoneMode,
      true,
    ),
  );

  const timeSlots = Array.from({ length: 48 }, (_, i) => {
    const totalMinutes = i * 30;
    const hours24 = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;

    const period = hours24 >= 12 ? "pm" : "am";
    const hours12 = hours24 % 12 || 12; // converts 0 → 12

    return `${hours12}:${String(minutes).padStart(2, "0")}${period}`;
  });

  const toKey = (status) => status.toLowerCase().replace(/\s+/g, "-");

  const groupByStatus = (data) => {
    // Step 1: create empty columns for all statuses
    const initialColumns = STATUS_LIST.reduce((cols, status) => {
      const key = toKey(status);
      cols[key] = { id: key, title: status, calls: [] };
      return cols;
    }, {});

    // Step 2: fill calls into each column
    data.forEach((item) => {
      const key = toKey(item.status);
      if (!initialColumns[key]) return;

      initialColumns[key].calls.push(item);
    });

    return { columns: initialColumns };
  };

  useEffect(() => {
    const calculateLinePosition = () => {
      const PIXELS_PER_MINUTE = PIXELS_PER_HEIGHT / 30;
      const now = dayjs().tz(timezoneMode.timezoneMode);

      // Calculate minutes since midnight
      const currentMinutes = now.hour() * 60 + now.minute();

      // Calculate top position (53px is the header height, adjust if needed)
      const top = 53 + currentMinutes * PIXELS_PER_MINUTE;

      setCurrentTimeLinePosition(top);
    };

    // Calculate immediately
    calculateLinePosition();

    // Update every minute
    const timer = setInterval(calculateLinePosition, 60000);

    return () => clearInterval(timer);
  }, [timezoneMode]);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(
        dayjs(dayjs().tz(timezoneMode.timezoneMode)).tz(
          timezoneMode.timezoneMode,
          true,
        ),
      );
    }, 60000);

    return () => clearInterval(timer);
  }, [timezoneMode]);

  useEffect(() => {
    if (searchedData && Array.isArray(searchedData)) {
      const grouped = groupByStatus(searchedData);
      setGroupedData(grouped);
    }
  }, [searchedData]);

  const getFormattedDate = (startDate, endDate) => {
    const start = dayjs(startDate).tz(timezoneMode.timezoneMode);
    const end = dayjs(endDate).tz(timezoneMode.timezoneMode);

    // Get the day of the week, month, and date for the start date
    const formattedDate = start.format("YY-MM-DD");

    // Get the time in 12-hour format with AM/PM
    const startTime = start.format("hh:mma");
    const endTime = end.format("hh:mma");

    return `${formattedDate} ⋅ ${startTime}-${endTime}`;
  };

  const handleToggleExpand = (id) => {
    setExpandedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  };

  const getColorFromStatus = (status) => {
    const found = allStatus.find((item) => item.status === status);
    return found ? found.color : null;
  };

  const formattedText = (text) => {
    return text.split("\n").map((line, index) => (
      <span key={index}>
        {line.split(" ").map((text) => (
          <span>{text} </span>
        ))}
        <br />
      </span>
    ));
  };

  const getDifferenceBetweenStartAndEnd = (startDate, endDate) => {
    const start = dayjs(startDate).tz(timezoneMode.timezoneMode);
    const end = dayjs(endDate).tz(timezoneMode.timezoneMode);

    const startMinutes = start.hour() * 60 + start.minute();
    const endMinutes = end.hour() * 60 + end.minute();

    return Number(endMinutes - startMinutes) > 0
      ? Number(endMinutes - startMinutes)
      : 30;
  };

  const getOverlapPositions = (interviews) => {
    if (!interviews.length) return [];

    // Sort interviews by start time
    const sorted = [...interviews].sort(
      (a, b) => dayjs(a.start_date).valueOf() - dayjs(b.start_date).valueOf(),
    );

    // Create timeline of all events (starts and ends)
    const events = [];
    sorted.forEach((interview, index) => {
      const start = dayjs(interview.start_date).tz(timezoneMode.timezoneMode);
      const end = dayjs(interview.end_date).tz(timezoneMode.timezoneMode);

      events.push({
        time: start,
        type: "start",
        interviewId: index,
        interview,
      });

      events.push({
        time: end,
        type: "end",
        interviewId: index,
        interview,
      });
    });

    // Sort events by time
    events.sort((a, b) => a.time.valueOf() - b.time.valueOf());

    // Track active interviews and their column assignments
    const activeInterviews = [];
    const result = [];

    // Process events in chronological order
    events.forEach((event) => {
      if (event.type === "start") {
        // Find first available column
        let columnIndex = 0;
        const usedColumns = activeInterviews.map((a) => a.column);

        while (usedColumns.includes(columnIndex)) {
          columnIndex++;
        }

        // Add to active interviews
        activeInterviews.push({
          ...event.interview,
          column: columnIndex,
          originalIndex: event.interviewId,
        });

        // Calculate total columns needed at this moment
        const totalColumns =
          Math.max(...activeInterviews.map((a) => a.column)) + 1;

        // Update all active interviews with current total columns
        activeInterviews.forEach((active) => {
          const existingResult = result.find(
            (r) => r.originalIndex === active.originalIndex,
          );
          if (existingResult) {
            existingResult.totalOverlaps = Math.max(
              existingResult.totalOverlaps || 1,
              totalColumns,
            );
          } else {
            result.push({
              ...active,
              totalOverlaps: totalColumns,
            });
          }
        });
      } else if (event.type === "end") {
        // Remove from active interviews
        const index = activeInterviews.findIndex(
          (a) => a.originalIndex === event.interviewId,
        );
        if (index !== -1) {
          activeInterviews.splice(index, 1);
        }
      }
    });

    return result;
  };

  const getPositionStyle = (
    startDate,
    endDate,
    overlapColumn = 0,
    totalOverlaps = 1,
  ) => {
    const PIXELS_PER_MINUTE = PIXELS_PER_HEIGHT / 30;
    const start = dayjs(startDate).tz(timezoneMode.timezoneMode);
    const end = dayjs(endDate).tz(timezoneMode.timezoneMode);

    const startMinutes = start.hour() * 60 + start.minute();
    const endMinutes = end.hour() * 60 + end.minute();

    const top = startMinutes * PIXELS_PER_MINUTE;
    const height = (endMinutes - startMinutes) * PIXELS_PER_MINUTE;

    // Calculate width based on maximum concurrent interviews during this interview's duration
    const width = totalOverlaps > 1 ? `${100 / totalOverlaps}%` : "100%";
    const left =
      totalOverlaps > 1 ? `${(100 / totalOverlaps) * overlapColumn}%` : "0%";

    return {
      top: `${top}px`,
      height: `${height}px`,
      width,
      left,
      position: "absolute",
    };
  };

  return (
    <div className="callHorizonViewSection">
      <div
        className="call-current-time-line"
        style={{ top: currentTimeLinePosition }}
      ></div>
      <div className="call-horizontal-time-rulers">
        <div className="call-horizontal-time-rulers-title">&nbsp;</div>
        {timeSlots.map((time, index) => (
          <div
            key={index}
            className={`call-horizontal-time-rulers-item ${
              !themeMode.themeMode
                ? "call-horizontal-time-rulers-item-light"
                : "call-horizontal-time-rulers-item-dark"
            }`}
          >
            {time}
          </div>
        ))}
      </div>
      {Object.values(groupedData.columns).map((column) => {
        const positionedInterviews = getOverlapPositions(column.calls);

        return (
          <div
            key={column.id}
            className={`call-horizontal-column ${
              !themeMode.themeMode
                ? "horizontal-light-mode"
                : "horizontal-dark-mode"
            }`}
          >
            <div
              className="call-pasted-overlay"
              style={{ height: `${currentTimeLinePosition}px` }}
            ></div>

            <h3 className="call-horizontal-title">
              {column.title} ({column.calls.length})
            </h3>

            <div className="call-horizontal-list-time-lines">
              {timeSlots.map((time, index) => (
                <div
                  key={index}
                  className={`call-horizontal-time-rulers-line ${
                    !themeMode.themeMode
                      ? "call-horizontal-time-rulers-line-light"
                      : "call-horizontal-time-rulers-line-dark"
                  }`}
                  style={{
                    top: index * PIXELS_PER_HEIGHT,
                  }}
                ></div>
              ))}
            </div>

            <div className="call-horizontal-list">
              {positionedInterviews.map((interview) => {
                const interviewStart = dayjs(
                  dayjs(interview.start_date).tz(timezoneMode.timezoneMode),
                ).tz(timezoneMode.timezoneMode, true);
                const isUpcoming = interviewStart.isAfter(currentTime);

                const position = getPositionStyle(
                  interview.start_date,
                  interview.end_date,
                  interview.column,
                  interview.totalOverlaps,
                );

                const diff = getDifferenceBetweenStartAndEnd(
                  interview.start_date,
                  interview.end_date,
                );

                return (
                  <Tooltip
                    key={interview.id}
                    placement="left"
                    overlayClassName="interview-hover-tooltip"
                    title={
                      <div
                        key={interview.id}
                        className={`call-horizontal-card ${
                          !themeMode.themeMode
                            ? "horizontal-card-light-mode"
                            : "horizontal-card-dark-mode"
                        } ${isUpcoming ? "highlight-upcoming" : ""}`}
                        style={{
                          "--pulse-color": interview.caller_color,
                        }}
                      >
                        <div className="call-horizontal-card-item">
                          <div
                            className="card-bar"
                            style={{ backgroundColor: interview.caller_color }}
                          ></div>
                          <div
                            className="card-item"
                            onClick={() => handleClickEdit(interview)}
                          >
                            <Tooltip
                              placement="top"
                              title={interview.company_name}
                            >
                              <div className="card-item-company">
                                {interview.company_name}
                              </div>
                            </Tooltip>
                            <div className="card-item-time">
                              {getFormattedDate(
                                interview.start_date,
                                interview.end_date,
                              )}
                            </div>
                            <div className="card-item-action">
                              <div className="card-item-action-btns">
                                <Tooltip
                                  placement="top"
                                  title="Job Description"
                                >
                                  {!isEmpty(interview.company_name) ? (
                                    <Button
                                      type="text"
                                      size="small"
                                      icon={<FileTextOutlined />}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleDownloadJD(
                                          interview.job_desc,
                                          interview.company_name,
                                        );
                                      }}
                                    />
                                  ) : (
                                    <Button
                                      disabled
                                      size="small"
                                      color="danger"
                                      variant="dashed"
                                      icon={<StopOutlined />}
                                    />
                                  )}
                                </Tooltip>
                                <Tooltip placement="top" title="PDF Resume">
                                  {!isEmpty(interview.pdf_url) ? (
                                    <Button
                                      type="text"
                                      size="small"
                                      icon={<FilePdfOutlined />}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleDownloadPDF(
                                          interview.pdf_url,
                                          interview.pdf_filename,
                                        );
                                      }}
                                    />
                                  ) : (
                                    <Button
                                      disabled
                                      size="small"
                                      color="danger"
                                      variant="dashed"
                                      icon={<StopOutlined />}
                                    />
                                  )}
                                </Tooltip>
                                <Tooltip placement="top" title="Docx Resume">
                                  {!isEmpty(interview.docx_url) ? (
                                    <Button
                                      type="text"
                                      size="small"
                                      icon={<FileWordOutlined />}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleDownloadResume(
                                          interview.docx_url,
                                        );
                                      }}
                                    />
                                  ) : (
                                    <Button
                                      disabled
                                      size="small"
                                      color="danger"
                                      variant="dashed"
                                      icon={<StopOutlined />}
                                    />
                                  )}
                                </Tooltip>
                                {canUseSpecialActions && (
                                  <Tooltip placement="top" title="Copy Prompt">
                                    {!isEmpty(interview.docx_url) ? (
                                      <Button
                                        type="text"
                                        size="small"
                                        icon={<CopyOutlined />}
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleCopyDetail(interview);
                                        }}
                                      />
                                    ) : (
                                      <Button
                                        disabled
                                        size="small"
                                        color="danger"
                                        variant="dashed"
                                        icon={<StopOutlined />}
                                      />
                                    )}
                                  </Tooltip>
                                )}
                              </div>
                              <Button
                                type="text"
                                size="small"
                                icon={
                                  expandedIds.includes(interview.id) ? (
                                    <MinusCircleOutlined />
                                  ) : (
                                    <PlusCircleOutlined />
                                  )
                                }
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleToggleExpand(interview.id);
                                }}
                              />
                            </div>
                          </div>
                          <div className="card-avatar">
                            <Tooltip placement="top" title={interview.caller}>
                              <img
                                src={interview.caller_avatar}
                                alt="avatar"
                                className="card-caller-avatar"
                              />
                            </Tooltip>
                            <Tooltip placement="top" title={interview.bidder}>
                              <img
                                src={interview.bidder_avatar}
                                alt="avatar"
                                className="card-bidder-avatar"
                              />
                            </Tooltip>
                          </div>
                        </div>

                        <AnimatePresence>
                          {expandedIds.includes(interview.id) &&
                            (isEmpty(interview.call_log) ? (
                              <></>
                            ) : (
                              <motion.div
                                className="card-extra-content"
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                exit={{ opacity: 0 }}
                              >
                                {interview.call_log.map((item, index) => {
                                  return (
                                    <div className="card-extra-item">
                                      <div className="card-extra-header">
                                        <Tag
                                          color={getColorFromStatus(
                                            item.status === 0
                                              ? "Waiting"
                                              : item.status === 1
                                                ? "Recruiter Phone"
                                                : item.status === 2
                                                  ? "Recruiter Video"
                                                  : item.status === 7
                                                    ? "HR Call"
                                                    : item.status === 3
                                                      ? "Technical"
                                                      : item.status === 4
                                                        ? "Behavioural"
                                                        : item.status === 5
                                                          ? "Final"
                                                          : "Failed",
                                          )}
                                        >
                                          {item.status === 0
                                            ? "Waiting"
                                            : item.status === 1
                                              ? "Recruiter Phone"
                                              : item.status === 2
                                                ? "Recruiter Video"
                                                : item.status === 7
                                                  ? "HR Call"
                                                  : item.status === 3
                                                    ? "Technical"
                                                    : item.status === 4
                                                      ? "Behavioural"
                                                      : item.status === 5
                                                        ? "Final"
                                                        : "Failed"}
                                        </Tag>
                                        <Button
                                          type="primary"
                                          size="small"
                                          icon={<EditOutlined />}
                                          onClick={() =>
                                            handleReviewModalOpen(
                                              item,
                                              interview.id,
                                            )
                                          }
                                        />
                                      </div>
                                      <div className="card-extra-body">
                                        {isEmpty(item?.desc) ? (
                                          <></>
                                        ) : (
                                          <div className="card-extra-desc">
                                            {formattedText(item.desc)}
                                          </div>
                                        )}
                                        {isEmpty(item?.feeling) ? (
                                          <></>
                                        ) : (
                                          <Rate
                                            tooltips={rateValues}
                                            value={
                                              isEmpty(item?.feeling)
                                                ? 0
                                                : item.feeling
                                            }
                                            character={
                                              <StarFilled
                                                style={{ fontSize: 12 }}
                                              />
                                            }
                                            disabled
                                          />
                                        )}
                                      </div>
                                    </div>
                                  );
                                })}
                              </motion.div>
                            ))}
                        </AnimatePresence>
                      </div>
                    }
                  >
                    <div
                      className="call-horizontal-list-temp"
                      style={{
                        ...position,
                      }}
                    >
                      <div
                        className={`call-horizontal-list-subtemp ${
                          !themeMode.themeMode
                            ? "horizontal-card-light-mode"
                            : "horizontal-card-dark-mode"
                        } ${isUpcoming ? "highlight-upcoming" : ""}`}
                        style={{
                          "--pulse-color": interview.caller_color,
                          backgroundColor: interview.caller_color,
                        }}
                        onClick={() => handleClickEdit(interview)}
                      >
                        <div
                          className={`card-item-sub-company ${diff < 25 ? "card-item-sub-company-small" : ""}`}
                        >
                          <div
                            className="card-item-company"
                            style={{ color: interview.caller_textcolor }}
                          >
                            {interview.company_name}
                          </div>
                        </div>
                        <div
                          className={`card-avatar ${diff < 25 ? "card-avatar-small" : ""}`}
                        >
                          <Tooltip placement="top" title={interview.caller}>
                            <img
                              src={interview.caller_avatar}
                              alt="avatar"
                              className={`card-caller-avatar ${diff < 25 ? "card-caller-avatar-small" : ""}`}
                            />
                          </Tooltip>
                        </div>
                      </div>
                    </div>
                  </Tooltip>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default CallCalendarView;
